import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { onValue, ref } from "firebase/database";
import { database } from "../../configs";
import {
  getUserId,
  parseUTCDate,
  setUserProps,
  trackEvent,
  trackPixelEvent,
} from "../../helpers";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  PLAN_FREE,
  PLAN_TRIAL,
} from "../../features/admin-panel/constants/planTypes";
import { getPlanPrice, normalizeBillingPeriod } from "../../constants";
import dayjs from "dayjs";

export default class Subscription {
  mainStore;

  data = null;
  isSubscriptionInfoOpen = true;
  user = null;
  hasSeenInitialData = false;

  constructor(mainStore) {
    makeObservable(this, {
      data: observable,
      setData: action,
      trialDaysLeft: computed,
      hasNeverSubscribed: computed,
      isSubscriptionLoaded: computed,
      isSubscriptionInfoOpen: observable,
      setIsSubscriptionInfoOpen: action,
      user: observable,
    });

    this.mainStore = mainStore;

    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      runInAction(() => {
        this.user = user;
      });
    });
  }

  async index() {
    const userID = await getUserId();

    const refValue = ref(database, `${userID}/PayPro/subscription`);

    await onValue(refValue, (data) => {
      this.setData(data.val());
    });
  }

  setData(value) {
    const previousStatus = this.data?.subscription_status_name;
    const nextData = value || {};
    const nextStatus = nextData.subscription_status_name;

    if (
      this.hasSeenInitialData &&
      previousStatus !== "Active" &&
      nextStatus === "Active"
    ) {
      const params = new URLSearchParams(nextData.checkout_query_string || "");
      const planIdentifier = params.get("planIdentifier");
      const billingPeriod = normalizeBillingPeriod(params.get("billingPeriod"));
      const purchaseValue = getPlanPrice(planIdentifier, billingPeriod);

      trackEvent("purchase", {
        transaction_id: String(nextData.order_id || ""),
        currency: "USD",
        value: purchaseValue,
        billing_period: billingPeriod,
        items: planIdentifier
          ? [{ item_id: planIdentifier, item_name: planIdentifier }]
          : undefined,
      });

      trackPixelEvent("Purchase", {
        currency: "USD",
        value: purchaseValue,
        content_ids: planIdentifier ? [planIdentifier] : undefined,
        content_type: "product",
      });
    }

    this.data = nextData;
    this.hasSeenInitialData = true;

    setUserProps({
      subscription_status: nextStatus || "none",
    });
  }

  get hasNeverSubscribed() {
    const data = this.data;

    return !data || !Object.keys(data).length;
  }

  get isSubscriptionLoaded() {
    return this.data !== null && this.user !== null;
  }

  get currentPlan() {
    const data = this.data;
    const trialDaysLeft = this.trialDaysLeft;

    if ((!data || !Object.keys(data).length) && trialDaysLeft > 0) {
      return PLAN_TRIAL;
    }

    const isActive = data?.subscription_status_name === "Active";
    const isFinished = data?.subscription_status_name === "Finished";
    const subscriptionFinishDateObj = parseUTCDate(
      data?.subscription_finish_date,
    );
    const isFinishedButActive =
      isFinished && subscriptionFinishDateObj?.isAfter(dayjs());

    if (!isActive && !isFinishedButActive) {
      return PLAN_FREE;
    }

    try {
      const params = new URLSearchParams(data?.checkout_query_string || "");
      return params.get("planIdentifier");
    } catch {
      return PLAN_FREE;
    }
  }

  // Billing period of the current subscription. Legacy subscriptions have no
  // billingPeriod in their checkout query string and are treated as monthly.
  get currentBillingPeriod() {
    try {
      const params = new URLSearchParams(
        this.data?.checkout_query_string || "",
      );
      return normalizeBillingPeriod(params.get("billingPeriod"));
    } catch {
      return normalizeBillingPeriod(null);
    }
  }

  get trialDaysLeft() {
    const user = this.user;

    if (!user) {
      return null;
    }

    const creationTime = user.metadata.creationTime;
    if (!creationTime) {
      return null;
    }

    const trialDays = 15;
    const startDate = new Date(creationTime);
    const now = new Date();
    const diffTime =
      startDate.getTime() + trialDays * 24 * 60 * 60 * 1000 - now.getTime();
    const daysLeft = Math.ceil(diffTime / (24 * 60 * 60 * 1000));

    return daysLeft > 0 ? daysLeft : 0;
  }

  setIsSubscriptionInfoOpen(value) {
    this.isSubscriptionInfoOpen = value;
  }
}
